import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, Calendar, TrendingUp, Users, DollarSign, Trophy, Plus } from "lucide-react";

const VenueAdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [revenueData, setRevenueData] = useState([]);

  useEffect(() => {
    if (user?.role !== "venue_owner") {
      navigate("/");
      return;
    }
    fetchDashboardData();
    fetchBookings();
    fetchRevenue();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/venue-admin/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/venue-admin/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  const fetchRevenue = async () => {
    try {
      const response = await axios.get(`${API}/venue-admin/revenue?period=month`);
      setRevenueData(response.data);
    } catch (error) {
      console.error("Error fetching revenue:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" data-testid="venue-admin-dashboard">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Yönetici Paneli
          </h1>
          <p className="text-gray-600">Halı saha işletmenizi yönetin</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Building2 className="h-8 w-8 text-blue-600" />
                <span className="text-3xl font-bold">{stats.totalVenues || 0}</span>
              </div>
              <p className="text-sm text-gray-600">Sahalar</p>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Calendar className="h-8 w-8 text-emerald-600" />
                <span className="text-3xl font-bold">{stats.totalBookings || 0}</span>
              </div>
              <p className="text-sm text-gray-600">Toplam Rezervasyon</p>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="h-8 w-8 text-green-600" />
                <span className="text-3xl font-bold">{(stats.totalRevenue || 0).toFixed(0)}</span>
              </div>
              <p className="text-sm text-gray-600">Toplam Gelir (TL)</p>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Users className="h-8 w-8 text-purple-600" />
                <span className="text-3xl font-bold">{stats.uniquePlayers || 0}</span>
              </div>
              <p className="text-sm text-gray-600">Oyuncu</p>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Stats */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                Bu Ay
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                  <span className="font-medium">Rezervasyonlar</span>
                  <span className="text-2xl font-bold text-blue-600">{stats.monthlyBookings || 0}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                  <span className="font-medium">Gelir</span>
                  <span className="text-2xl font-bold text-green-600">{(stats.monthlyRevenue || 0).toFixed(2)} TL</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
                  <span className="font-medium">Maçlar</span>
                  <span className="text-2xl font-bold text-purple-600">{stats.totalMatches || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-600" />
                Sahalarım
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData?.venues?.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">Henüz saha eklemediniz</p>
                  <Button 
                    onClick={() => navigate("/venues/add")}
                    className="bg-gradient-to-r from-blue-500 to-indigo-600"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    İlk Sahanı Ekle
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {dashboardData?.venues?.slice(0, 3).map((venue) => (
                    <div 
                      key={venue.id}
                      className="p-3 border rounded-lg hover:border-blue-500 transition-colors cursor-pointer"
                      onClick={() => navigate(`/venues/${venue.id}`)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-semibold">{venue.name}</h4>
                          <p className="text-sm text-gray-600">{venue.location}</p>
                        </div>
                        <Badge className="bg-blue-100 text-blue-700">
                          {venue.pricePerHour} TL/saat
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {dashboardData?.venues?.length > 3 && (
                    <Button variant="outline" className="w-full">
                      Tümünü Gör ({dashboardData.venues.length})
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="bookings">Rezervasyonlar</TabsTrigger>
            <TabsTrigger value="matches">Yaklaşan Maçlar</TabsTrigger>
            <TabsTrigger value="revenue">Gelir Raporu</TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card>
              <CardHeader>
                <CardTitle>Rezervasyonlar</CardTitle>
                <CardDescription>Tüm saha rezervasyonları</CardDescription>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Henüz rezervasyon yok</p>
                ) : (
                  <div className="space-y-3">
                    {bookings.slice(0, 10).map((booking) => (
                      <div 
                        key={booking.id}
                        className="p-4 border rounded-lg hover:border-blue-500 transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold">{booking.venueName}</h4>
                            <p className="text-sm text-gray-600">{booking.userName}</p>
                            <p className="text-sm text-gray-600">
                              {booking.date} - {booking.time} ({booking.duration} dk)
                            </p>
                          </div>
                          <div className="text-right">
                            <Badge className={
                              booking.status === "confirmed" ? "bg-green-100 text-green-700" :
                              booking.status === "cancelled" ? "bg-red-100 text-red-700" :
                              "bg-gray-100 text-gray-700"
                            }>
                              {booking.status}
                            </Badge>
                            <p className="text-lg font-bold text-emerald-600 mt-2">{parseFloat(booking.price).toFixed(2)} TL</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Matches Tab */}
          <TabsContent value="matches">
            <Card>
              <CardHeader>
                <CardTitle>Yaklaşan Maçlar</CardTitle>
                <CardDescription>Sahalarınızda planlanmış maçlar</CardDescription>
              </CardHeader>
              <CardContent>
                {dashboardData?.upcomingMatches?.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Yaklaşan maç yok</p>
                ) : (
                  <div className="space-y-3">
                    {dashboardData?.upcomingMatches?.map((match) => (
                      <div 
                        key={match.id}
                        className="p-4 border rounded-lg hover:border-blue-500 transition-colors cursor-pointer"
                        onClick={() => navigate(`/matches/${match.id}`)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold">{match.venueName}</h4>
                            <p className="text-sm text-gray-600">Kaptan: {match.captainName}</p>
                            <p className="text-sm text-gray-600">
                              {match.date} - {match.time}
                            </p>
                          </div>
                          <Badge className="bg-emerald-100 text-emerald-700">
                            {match.players?.length || 0}/{match.maxPlayers} Oyuncu
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue">
            <Card>
              <CardHeader>
                <CardTitle>Gelir Raporu</CardTitle>
                <CardDescription>Aylık gelir analizi</CardDescription>
              </CardHeader>
              <CardContent>
                {revenueData.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Gelir verisi yok</p>
                ) : (
                  <div className="space-y-4">
                    {revenueData.map((data) => (
                      <div key={data.period} className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-semibold text-lg">{data.period}</h4>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-green-600">{data.revenue.toFixed(2)} TL</p>
                            <p className="text-sm text-gray-600">{data.bookings} rezervasyon</p>
                          </div>
                        </div>
                        {Object.keys(data.venues).length > 0 && (
                          <div className="pt-3 border-t border-emerald-200">
                            <p className="text-xs text-gray-600 mb-2">Saha Bazında:</p>
                            <div className="grid grid-cols-2 gap-2">
                              {Object.entries(data.venues).map(([venueName, revenue]) => (
                                <div key={venueName} className="text-sm">
                                  <span className="text-gray-700">{venueName}:</span>
                                  <span className="font-semibold text-green-600 ml-2">{revenue.toFixed(2)} TL</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default VenueAdminDashboard;
